export interface ApiError {
  message: string;
  redirected: boolean;
  status: number;
  url: string;
}

interface ErrorLike {
  message?: string;
  error?: string;
  redirected?: boolean;
  status?: number;
}

type ApiResponseError = {
  error: string;
  message: string;
  status: number;
};

/**
 * Base path the SPA prepends to every API call (the backend's global prefix,
 * see `vite.config.mts`). Anything building an API URL by hand — a file link,
 * for instance — takes it from here rather than reading the environment again.
 */
export const apiBaseUrl = import.meta.env.VITE_API_SERVER || '';

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rejectWithValue?: (value: ApiError) => any
): Promise<T> {
  const url = `${apiBaseUrl}${path}`;

  try {
    // Browser-side SPA fetch; url is the app's own API base + an internal
    // app-relative path. Not server-side SSRF.
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
    });

    if (response.redirected) {
      const error: ApiError = { message: 'Redirect detected', redirected: response.redirected, status: response.status, url: response.url };
      if (rejectWithValue) return rejectWithValue(error);
      throw error;
    }

    if (!response.ok) {
      let errorMessage = response.status === 413 ? 'Die Datei ist zu groß.' : `API request failed (${response.status})`;
      try {
        const errorData = (await response.json()) as ApiResponseError;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If we can't parse JSON, use the default message
      }

      const error: ApiError = { message: errorMessage, redirected: response.redirected, status: response.status, url: response.url };
      if (rejectWithValue) return rejectWithValue(error);
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    let json: T;
    try {
      json = (await response.json()) as T;
    } catch {
      const apiError: ApiError = {
        message: 'Invalid JSON response from server',
        redirected: false,
        status: response.status,
        url: url,
      };
      if (rejectWithValue) return rejectWithValue(apiError);
      throw apiError;
    }

    if (typeof json === 'object' && json !== null && 'error' in json) {
      const responseError = json as unknown as ApiResponseError;
      const error: ApiError = { message: responseError.message || `API request failed (${JSON.stringify(responseError.error)})`, redirected: response.redirected, status: response.status, url: response.url };
      if (rejectWithValue) return rejectWithValue(error);
      throw error;
    }

    return json;
  } catch (error: unknown) {
    const err = error as ErrorLike;
    const errorMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : err.message || err.error || 'Network error occurred';

    const apiError: ApiError = {
      message: errorMessage,
      redirected: err.redirected || false,
      status: err.status || 0,
      url: url,
    };
    if (rejectWithValue) return rejectWithValue(apiError);
    throw apiError;
  }
}
